import { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import * as Ri from "react-icons/ri";
import EdocertLogo from "@assets/edocert-logo.svg";
import HamburgerIcon from "@assets/hamburger.svg";
import { IoIosArrowDown } from "react-icons/io";
import { Dropdown } from "flowbite-react";
import { useSocketConnection } from "../../hooks/useSocketConnection";
import { NotificationPopover } from "@/components/ui/NotificationPopover";
import { NotificationsPanel } from "@/components/notifications/NotificationsPanel";
import { useMenu, DESIGN_MENU_ITEMS } from "@/hooks/useMenu";
import type { MenuItem } from "@/core/api/types/menu";
import { useUnreadCount } from "@/modules/Notifications/hooks/useUnreadCount";
import { TokenStorage } from "@/core/api";
import { useSidebarStore } from "@/stores/sidebar.store";
import { RiUserFill } from "react-icons/ri";

export default function DashboardLayout() {
  const { isOpen: sidebarOpen, toggleSidebar } = useSidebarStore();
  const { menuItems } = useMenu();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [openMenuIds, setOpenMenuIds] = useState<string[]>([]);
  const [devMenuOpen, setDevMenuOpen] = useState(false);


  // Trova la prima route disponibile nel menu
  const findFirstRoute = (items: MenuItem[]): string | null => {
    for (const item of items) {
      if (item.route) {
        return item.route;
      }
      if (item.childs && item.childs.length > 0) {
        for (const child of item.childs) {
          if (child.route) {
            return child.route;
          }
        }
      }
    }
    return null;
  };

  // Redirect alla prima route se siamo su /dashboard
  useEffect(() => {
    if (menuItems && menuItems.length > 0 && location.pathname === "/dashboard") {
      const firstRoute = findFirstRoute(menuItems);
      if (firstRoute) {
        navigate(`/${firstRoute}`, { replace: true });
      }
    }
  }, [menuItems, location.pathname, navigate]);

  // Auto-expand menu based on current path
  useEffect(() => {
    if (menuItems && menuItems.length > 0) {
      const currentPath = location.pathname;
      menuItems.forEach((item) => {
        if (item.childs && item.childs.length > 0) {
          const hasActiveChild = item.childs.some((child) => {
            const childRoute = child.route ? `/${child.route}` : null;
            return childRoute && currentPath.startsWith(childRoute);
          });
          if (hasActiveChild && !openMenuIds.includes(item.id_menu)) {
            setOpenMenuIds((prev) => [...prev, item.id_menu]);
          }
        }
      });
    }
  }, [menuItems, location.pathname]);

  const toggleSubmenu = (menuId: string) => {
    setOpenMenuIds((prev) =>
      prev.includes(menuId)
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId]
    );
  };

  // Estrai la sezione dal path (primo segmento dopo get_page)
  // "get_page/corsi/fad/1" → "corsi"
  // "get_page/users/discenti" → "users"
  const getSection = (path: string): string | null => {
    const cleanPath = path.replace(/^\//, '');
    const parts = cleanPath.split('/');
    return parts[1] || null; // parts[0] = "get_page", parts[1] = sezione
  };

  const isMenuItemActive = (item: MenuItem) => {
    const currentSection = getSection(location.pathname);
    if (!currentSection) return false;

    // Per item con route diretta
    if (item.route) {
      const itemSection = getSection(item.route);
      if (itemSection === currentSection) return true;
    }

    // Per parent con children
    if (item.childs && item.childs.length > 0) {
      return item.childs.some((child) => {
        if (child.route) {
          const childSection = getSection(child.route);
          return childSection === currentSection;
        }
        return false;
      });
    }

    return false;
  };

  const { unreadCount } = useUnreadCount();

  useSocketConnection();

  const handleLogout = () => {
    TokenStorage.removeTokens();
    queryClient.clear();
    toast.success("Logout effettuato con successo");
    navigate("/login", { replace: true });
  };

  const handleMenuClick = (item: MenuItem) => {
    if (item.childs && item.childs.length > 0) {
      toggleSubmenu(item.id_menu);
    } else if (item.route) {
      navigate(`/${item.route}`);
    }
  };

  return (
    <div className="flex overflow-hidden w-screen h-screen bg-white">
      {/* Sidebar */}
      <div
        className={`bg-white text-gray-700 p-0 transform transition-all duration-300 ease-in-out fixed h-full border-r border-gray-200 overflow-hidden
          ${sidebarOpen ? "w-64 translate-x-0" : "w-0 -translate-x-full"}`}
      >
        <nav
          className={`w-64 transition-transform duration-300 ease-in-out mt-0 ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <img src={EdocertLogo} className="w-[160px] mb-2 ml-4 mt-4" />

          <ul className="mt-4 space-y-2">
            {menuItems?.map((item: MenuItem) => (
              <li key={item.id_menu}>
                {item.childs && item.childs.length > 0 ? (
                  <>
                    <div
                      className={`flex items-center px-4 py-3 cursor-pointer hover:bg-gray-100 ${
                        isMenuItemActive(item)
                          ? "bg-[#B80F151A] text-[#B80F15] font-semibold"
                          : ""
                      }`}
                      onClick={() => handleMenuClick(item)}
                    >
                      {item.left_icon && (
                        <span className="mr-3">
                          <i className={`${item.left_icon} text-xl`}></i>
                        </span>
                      )}
                      <span>{item.item_label}</span>
                      {item.in_test === "1" && (
                        <span className="ml-2 px-1.5 py-0.5 text-[9px] font-semibold uppercase bg-amber-100 text-amber-700 rounded">
                          design
                        </span>
                      )}
                      <span
                        className={`ml-auto transform transition-transform duration-200 ${
                          openMenuIds.includes(item.id_menu) ? "rotate-180" : ""
                        }`}
                      >
                        <IoIosArrowDown />
                      </span>
                    </div>
                    <div
                      className="overflow-hidden transition-all duration-300 ease-in-out"
                      style={{
                        maxHeight: openMenuIds.includes(item.id_menu)
                          ? `${item.childs.length * 40}px`
                          : "0",
                        opacity: openMenuIds.includes(item.id_menu) ? 1 : 0,
                      }}
                    >
                      <ul className="py-2 ml-12 space-y-2">
                        {item.childs.map((subItem: MenuItem) => {
                          const subRoute = subItem.route ? `/${subItem.route}` : null;
                          // Controlla se il child è attivo basandosi sulla sezione
                          const childSection = subItem.route ? getSection(subItem.route) : null;
                          const currentSection = getSection(location.pathname);
                          const isChildActive = childSection && currentSection && childSection === currentSection;
                          return (
                            <li key={subItem.id_menu}>
                              {subRoute ? (
                                <Link
                                  to={subRoute}
                                  className={`block cursor-pointer hover:text-[#B80F15] transition-colors duration-200 ${
                                    isChildActive ? "text-[#B80F15]" : ""
                                  }`}
                                >
                                  {subItem.item_label}
                                </Link>
                              ) : (
                                <span className="block text-gray-400 cursor-default">
                                  {subItem.item_label}
                                </span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </>
                ) : (
                  <div
                    className={`flex items-center px-4 py-3 cursor-pointer hover:bg-gray-100 ${
                      isMenuItemActive(item)
                        ? "bg-[#B80F151A] text-[#B80F15] font-semibold"
                        : ""
                    } ${!item.route ? "text-gray-400 cursor-default" : ""}`}
                    onClick={() => handleMenuClick(item)}
                  >
                    {item.left_icon && (
                      <span className="mr-3">
                        <i className={`${item.left_icon} text-xl`}></i>
                      </span>
                    )}
                    <span>{item.item_label}</span>
                    {item.in_test === "1" && (
                      <span className="ml-2 px-1.5 py-0.5 text-[9px] font-semibold uppercase bg-amber-100 text-amber-700 rounded">
                        design
                      </span>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>

          {/* Sezione Sviluppo */}
          <div className="mt-4 mx-3 border-t border-dashed border-gray-300 pt-3">
            <div
              className="flex items-center px-1 py-2 cursor-pointer rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              onClick={() => setDevMenuOpen((v) => !v)}
            >
              <span className="mr-2 text-xs font-semibold uppercase tracking-wider">
                Sviluppo
              </span>
              <span
                className={`ml-auto transform transition-transform duration-200 ${devMenuOpen ? "rotate-180" : ""}`}
              >
                <IoIosArrowDown size={13} />
              </span>
            </div>
            <div
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{
                maxHeight: devMenuOpen ? "500px" : "0",
                opacity: devMenuOpen ? 1 : 0,
              }}
            >
              <ul className="mt-1 ml-3 space-y-1">
                {DESIGN_MENU_ITEMS.map((item) =>
                  item.childs && item.childs.length > 0 ? (
                    <li key={item.id_menu}>
                      <span className="block text-xs font-semibold uppercase tracking-wide text-gray-400 px-2 pt-2 pb-1">
                        {item.item_label}
                      </span>
                      <ul className="ml-2 space-y-1">
                        {item.childs.map((child) => (
                          <li key={child.id_menu}>
                            <Link
                              to={`/${child.route}`}
                              className={`block text-sm py-1 px-2 rounded transition-colors duration-200 ${
                                location.pathname === `/${child.route}`
                                  ? "text-[#B80F15]"
                                  : "text-gray-400 hover:text-gray-600"
                              }`}
                            >
                              {child.item_label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ) : (
                    <li key={item.id_menu}>
                      <Link
                        to={`/${item.route}`}
                        className={`block text-sm py-1 px-2 rounded transition-colors duration-200 ${
                          location.pathname === `/${item.route}`
                            ? "text-[#B80F15]"
                            : "text-gray-400 hover:text-gray-600"
                        }`}
                      >
                        {item.item_label}
                      </Link>
                    </li>
                  )
                )}
              </ul>
            </div>
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div
        className={`flex-1 flex flex-col transition-all duration-300 ease-in-out relative
          ${sidebarOpen ? "ml-64" : "ml-0"}`}
      >
        {/* Navbar */}
        <div className="bg-white border-b border-b-gray-200 p-4 flex justify-between items-center text-black h-[61px] box-border ">
          <div className="flex items-center justify-end w-full">
            <div className="flex relative item-center">
              <NotificationPopover
                trigger={
                  <div className="relative mr-5">
                    <Ri.RiNotification2Line size={24} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-2 -right-2 flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold bg-edPrimaryColor-500 text-white rounded-full px-1">
                        {unreadCount}
                      </span>
                    )}
                  </div>
                }
              >
                <NotificationsPanel />
              </NotificationPopover>
            </div>
            <button
              onClick={toggleSidebar}
              className={`w-10 h-10 rounded-full border border-gray-300 bg-white outline-none ring-0 shadow-none flex items-center justify-center hover:bg-gray-50 transition-colors duration-200 absolute focus:outline-none focus:ring-0 focus:shadow-none focus:border-gray-300 ${
                sidebarOpen ? "left-[-21px]" : "left-[20px]"
              } top-2.5`}
            >
              <img
                src={HamburgerIcon}
                className={`w-5 text-gray-600 transition-transform duration-300 ${
                  !sidebarOpen ? "scale-x-[-1]" : ""
                }`}
              />
            </button>
            <Dropdown
              label=""
              className="z-20 text-sm font-normal rounded-lg text-secondaryColor-500"
              dismissOnClick={true}
              renderTrigger={() => (
                <span>
                  <div
                    className="relative flex items-center justify-center text-white w-[35px] h-[35px] rounded-full bg-edPrimaryColor-500"
                    data-twe-dropdown-ref
                  >
                    <RiUserFill size={18} className="m-auto" />
                  </div>
                </span>
              )}
            >
              <Dropdown.Item onClick={handleLogout}>Logout</Dropdown.Item>
            </Dropdown>
          </div>
        </div>

        <Outlet />
      </div>
    </div>
  );
}
